import { clsx } from 'clsx';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { prefersReducedMotion } from '../lib/motion';
import s from './ui.module.css';

interface ToastOptions {
  error?: boolean;
  /** A moment worth marking (all areas assessed, a level confirmed): a short confetti burst. */
  celebrate?: boolean;
}

interface ToastItem extends ToastOptions {
  id: number;
  text: string;
}

const ToastContext = createContext<(text: string, opts?: ToastOptions) => void>(() => undefined);

/** Pieces of the confetti burst; each takes its angle and colour from its position (ui.module.css). */
const CONFETTI = 12;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const show = useCallback((text: string, opts?: ToastOptions) => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev.slice(-2), { id, text, ...opts }]);
    setTimeout(() => {
      setItems((prev) => prev.filter((i) => i.id !== id));
    }, 3200);
  }, []);
  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* One live region for every toast; errors are also alerts so they interrupt. */}
      <div className={s.toasts} role="status" aria-live="polite">
        {items.map((i) => (
          <div
            key={i.id}
            role={i.error ? 'alert' : undefined}
            className={clsx(s.toast, i.error && s.toastError, i.celebrate && s.toastCelebrate)}
          >
            {i.celebrate && !prefersReducedMotion() ? (
              <span className={s.confetti} aria-hidden="true">
                {Array.from({ length: CONFETTI }, (_, n) => (
                  <span key={n} />
                ))}
              </span>
            ) : null}
            {i.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
